import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { BookOpen, Plus, Star, Clock, Euro, Filter, X, Trash2, Heart } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// All countries/cuisines for recipe selection - sorted alphabetically
const ALL_COUNTRY_CUISINES = [
  'Afghaans', 'Albaans', 'Algerijns', 'Amerikaans', 'Angolees', 'Argentijns', 'Armeens', 'Australisch', 'Azerbeidzjaans',
  'Bahreins', 'Bangladeshi', 'Belgisch', 'Boliviaans', 'Bosnisch', 'Braziliaans', 'Brits', 'Bulgaars',
  'Cambodjaans', 'Canadees', 'Chileens', 'Chinees', 'Colombiaans', 'Congolees', 'Costaricaans', 'Cubaans', 'Cypriotisch', 'Tsjechisch',
  'Deens', 'Dominicaans', 'Duits',
  'Ecuadoraans', 'Egyptisch', 'Eritrees', 'Estisch', 'Ethiopisch',
  'Filipijns', 'Fins', 'Frans',
  'Georgisch', 'Ghanees', 'Grieks', 'Guatemalteeks',
  'Haïtiaans', 'Hondurees', 'Hongaars',
  'IJslands', 'Indiaas', 'Indisch', 'Indonesisch', 'Iraans', 'Iraaks', 'Iers', 'Israëlisch', 'Italiaans', 'Ivoriaans',
  'Jamaicaans', 'Japans', 'Jemenitisch', 'Joegoslavisch', 'Jordaans',
  'Kameroenees', 'Kazachs', 'Keniaans', 'Koreaans', 'Koeweitisch', 'Kroatisch',
  'Laotiaans', 'Lets', 'Libanees', 'Libisch', 'Litouws', 'Luxemburgs',
  'Macedonisch', 'Maleisisch', 'Marokkaans', 'Mauritiaans', 'Mexicaans', 'Moldavisch', 'Mongools',
  'Namibisch', 'Nepalees', 'Nederlands', 'Nicaraguaans', 'Nieuw-Zeelands', 'Nigeriaans', 'Noors',
  'Oegandees', 'Oekraïens', 'Omanitisch', 'Oostenrijks',
  'Pakistaans', 'Palestijns', 'Panameens', 'Paraguayaans', 'Peruviaans', 'Pools', 'Portugees', 'Puertoricoaans',
  'Qatarees',
  'Roemeens', 'Russisch', 'Rwandees',
  'Salvadoraans', 'Saoedi-Arabisch', 'Senegalees', 'Servisch', 'Singaporees', 'Sloveens', 'Slowaaks', 'Soedanees', 'Somalisch', 'Spaans', 'Sri Lankaans', 'Surinaams', 'Syrisch', 'Zweeds', 'Zwitsers',
  'Taiwanees', 'Tanzaniaans', 'Thais', 'Togolees', 'Tunesisch', 'Turks',
  'Uruguayaans',
  'Venezolaans', 'Vietnamees',
  'Zambiaans', 'Zimbabwaans', 'Zuid-Afrikaans',
  // General categories
  'Internationaal', 'Mediterraan', 'Midden-Oosters', 'Aziatisch', 'Afrikaans', 'Caribisch', 'Latijns-Amerikaans', 'Scandinavisch'
].sort();

// Subset for filters (most common cuisines)
const CUISINE_OPTIONS = ['Marokkaans', 'Turks', 'Surinaams', 'Indisch', 'Internationaal', 'Nederlands', 'Midden-Oosters', 'Italiaans', 'Mediterraan', 'Mexicaans', 'Aziatisch', 'Frans', 'Grieks', 'Thais', 'Indonesisch', 'Japans'];
const DIET_OPTIONS = ['vegetarian', 'vegan', 'halal', 'kosher', 'gluten-free', 'lactose-free', 'low-carb', 'keto'];

// Helper to convert tag to translation key (e.g., "gluten-free" -> "gluten_free")
const getTagKey = (tag) => tag.replace(/-/g, '_');

function RecipesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [filters, setFilters] = useState({ cuisine: '', dietary: '', maxTime: '' });
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  
  const [newRecipe, setNewRecipe] = useState({
    title: '',
    description: '',
    cuisine: '',
    cook_time_min: 30,
    servings: 4,
    dietary_tags: [],
    ingredients: [{ name: '', amount: '', unit: 'g' }],
    steps: ['']
  });

  useEffect(() => {
    fetchRecipes();
  }, [filters]);

  useEffect(() => {
    fetchFavoriteIds();
  }, []);

  const fetchFavoriteIds = async () => {
    try {
      const response = await axios.get(`${API}/favorites`, { withCredentials: true });
      const ids = new Set((response.data.favorites || []).map(f => f.recipe_id));
      setFavoriteIds(ids);
    } catch (error) {
      // Not logged in or error - ignore
    }
  };

  const toggleFavorite = async (e, recipeId) => {
    e.stopPropagation();
    try {
      if (favoriteIds.has(recipeId)) {
        await axios.delete(`${API}/favorites/${recipeId}`, { withCredentials: true });
        setFavoriteIds(prev => { const next = new Set(prev); next.delete(recipeId); return next; });
        toast.success(t('favorites.removed'));
      } else {
        await axios.post(`${API}/favorites/${recipeId}`, {}, { withCredentials: true });
        setFavoriteIds(prev => new Set(prev).add(recipeId));
        toast.success(t('recipe.added_to_favorites'));
      }
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const fetchRecipes = async () => {
    try {
      let url = `${API}/recipes?limit=30`;
      if (filters.cuisine && filters.cuisine !== 'all') url += `&cuisine=${filters.cuisine}`;
      if (filters.dietary && filters.dietary !== 'all') url += `&dietary_tags=${filters.dietary}`;
      const response = await axios.get(url);
      setRecipes(response.data.recipes);
    } catch (error) {
      console.error('Failed to fetch recipes:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddIngredient = () => {
    setNewRecipe({
      ...newRecipe,
      ingredients: [...newRecipe.ingredients, { name: '', amount: '', unit: 'g' }]
    });
  };

  const handleRemoveIngredient = (index) => {
    const updated = newRecipe.ingredients.filter((_, i) => i !== index);
    setNewRecipe({ ...newRecipe, ingredients: updated.length ? updated : [{ name: '', amount: '', unit: 'g' }] });
  };

  const handleIngredientChange = (index, field, value) => {
    const updated = [...newRecipe.ingredients];
    updated[index][field] = value;
    setNewRecipe({ ...newRecipe, ingredients: updated });
  };

  const handleAddStep = () => {
    setNewRecipe({ ...newRecipe, steps: [...newRecipe.steps, ''] });
  };

  const handleRemoveStep = (index) => {
    const updated = newRecipe.steps.filter((_, i) => i !== index);
    setNewRecipe({ ...newRecipe, steps: updated.length ? updated : [''] });
  };

  const handleStepChange = (index, value) => {
    const updated = [...newRecipe.steps];
    updated[index] = value;
    setNewRecipe({ ...newRecipe, steps: updated });
  };

  const toggleDietTag = (tag) => {
    const current = newRecipe.dietary_tags;
    if (current.includes(tag)) {
      setNewRecipe({ ...newRecipe, dietary_tags: current.filter(t => t !== tag) });
    } else {
      setNewRecipe({ ...newRecipe, dietary_tags: [...current, tag] });
    }
  };

  const resetForm = () => {
    setNewRecipe({
      title: '',
      description: '',
      cuisine: '',
      cook_time_min: 30,
      servings: 4,
      dietary_tags: [],
      ingredients: [{ name: '', amount: '', unit: 'g' }],
      steps: ['']
    });
  };

  const handleSubmitRecipe = async () => {
    if (!newRecipe.title.trim()) {
      toast.error(t('recipe.title_required'));
      return;
    }
    if (!newRecipe.ingredients[0].name.trim()) {
      toast.error(t('recipe.ingredient_required'));
      return;
    }
    if (!newRecipe.steps[0].trim()) {
      toast.error(t('recipe.step_required'));
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        title: newRecipe.title,
        description: newRecipe.description,
        cuisines: newRecipe.cuisine ? [newRecipe.cuisine] : ['General'],
        cook_time_min: parseInt(newRecipe.cook_time_min) || 30,
        servings: parseInt(newRecipe.servings) || 4,
        dietary_tags: newRecipe.dietary_tags,
        ingredients: newRecipe.ingredients
          .filter(ing => ing.name.trim())
          .map(ing => ({
            name: ing.name,
            amount: parseFloat(ing.amount) || 0,
            unit: ing.unit
          })),
        steps: newRecipe.steps.filter(s => s.trim())
      };

      await axios.post(`${API}/recipes`, payload, { withCredentials: true });
      toast.success(t('recipe.added_success'));
      setShowAddModal(false);
      resetForm();
      fetchRecipes();
    } catch (error) {
      console.error('Failed to add recipe:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="py-4 sm:py-8">
      <div className="container mx-auto px-3 sm:px-4 max-w-7xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-8">
          <div className="flex items-center gap-3">
            <div className="bg-primary rounded-full p-2 sm:p-3">
              <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-[#2C3E2D]" data-testid="recipes-title">
              {t('recipes.title')}
            </h1>
          </div>
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-secondary hover:bg-secondary-hover text-white w-full sm:w-auto"
            data-testid="add-recipe-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('recipes.add_recipe')}
          </Button>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              {t('recipes.filters')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">{t('recipes.kitchen')}</label>
                <Select value={filters.cuisine} onValueChange={(v) => setFilters({...filters, cuisine: v})}>
                  <SelectTrigger data-testid="filter-cuisine">
                    <SelectValue placeholder={t('recipes.all_kitchens')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('recipes.all_kitchens')}</SelectItem>
                    {CUISINE_OPTIONS.map(c => (
                      <SelectItem key={c} value={c}>{t(`cuisines.${c.toLowerCase().replace(/ /g, '_').replace(/-/g, '_')}`, c)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">{t('recipes.diet')}</label>
                <Select value={filters.dietary} onValueChange={(v) => setFilters({...filters, dietary: v})}>
                  <SelectTrigger data-testid="filter-dietary">
                    <SelectValue placeholder={t('recipes.all_diets')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('recipes.all_diets')}</SelectItem>
                    <SelectItem value="vegetarian">{t('recipes.vegetarian')}</SelectItem>
                    <SelectItem value="vegan">{t('recipes.vegan')}</SelectItem>
                    <SelectItem value="halal">{t('recipes.halal')}</SelectItem>
                    <SelectItem value="gluten-free">{t('recipes.gluten_free')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">{t('recipes.cook_time')}</label>
                <Select value={filters.maxTime} onValueChange={(v) => setFilters({...filters, maxTime: v})}>
                  <SelectTrigger data-testid="filter-time">
                    <SelectValue placeholder={t('recipes.all_times')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('recipes.all_times')}</SelectItem>
                    <SelectItem value="20">{t('recipes.under_20')}</SelectItem>
                    <SelectItem value="40">{t('recipes.time_20_40')}</SelectItem>
                    <SelectItem value="60">{t('recipes.time_40_60')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => setFilters({ cuisine: '', dietary: '', maxTime: '' })}
                  data-testid="reset-filters-btn"
                >
                  {t('recipes.reset_filters')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {recipes.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            {recipes.map((recipe) => (
              <Card 
                key={recipe.recipe_id} 
                className="hover:shadow-lg transition-shadow cursor-pointer group relative"
                onClick={() => navigate(`/recipe/${recipe.recipe_id}`)}
                data-testid={`recipe-card-${recipe.recipe_id}`}
              >
                <button
                  onClick={(e) => toggleFavorite(e, recipe.recipe_id)}
                  className="absolute top-3 right-3 z-10 p-2 rounded-full bg-white/80 hover:bg-white shadow-sm transition-colors"
                  data-testid={`fav-btn-${recipe.recipe_id}`}
                >
                  <Heart className={`w-4 h-4 ${favoriteIds.has(recipe.recipe_id) ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} />
                </button>
                <CardContent className="pt-6">
                  <h3 className="text-xl font-heading font-bold mb-2 group-hover:text-primary transition-colors">
                    {recipe.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{recipe.description}</p>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {recipe.cook_time_min} {t('common.min')}
                    </div>
                    <div className="flex items-center gap-1">
                      <Euro className="w-4 h-4" />
                      &euro;{recipe.price_estimate_eur?.toFixed(2)}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mb-3">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span className="text-sm">{recipe.ratings_avg?.toFixed(1) || '0.0'}</span>
                    <span className="text-xs text-gray-500">({recipe.ratings_count || 0})</span>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {(Array.isArray(recipe.cuisines) ? recipe.cuisines : [recipe.cuisines]).filter(Boolean).map((cuisine, idx) => (
                      <span key={idx} className="px-2 py-1 bg-primary/10 text-primary rounded text-xs">
                        {t(`cuisines.${cuisine.toLowerCase().replace(/ /g, '_').replace(/-/g, '_')}`, cuisine)}
                      </span>
                    ))}
                    {recipe.dietary_tags?.slice(0, 2).map((tag, idx) => (
                      <span key={idx} className="px-2 py-1 bg-success/10 text-success rounded text-xs">
                        {t(`recipes.${getTagKey(tag)}`, tag)}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">{t('recipes.no_recipes')}</h3>
            <p className="text-gray-500 mb-4">{t('recipes.adjust_filters')}</p>
            <Button onClick={() => setShowAddModal(true)} data-testid="add-recipe-empty-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t('recipes.add_recipe')}
            </Button>
          </div>
        )}

        {/* Add Recipe Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5" />
                {t('recipes.add_recipe')}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <Label>{t('recipe.title_label')} *</Label>
                  <Input
                    value={newRecipe.title}
                    onChange={(e) => setNewRecipe({ ...newRecipe, title: e.target.value })}
                    placeholder={t('recipe.title_placeholder')}
                    data-testid="recipe-title-input"
                  />
                </div>
                <div>
                  <Label>{t('recipe.description_label')}</Label>
                  <Textarea
                    value={newRecipe.description}
                    onChange={(e) => setNewRecipe({ ...newRecipe, description: e.target.value })}
                    placeholder={t('recipe.description_placeholder')}
                    rows={2}
                    data-testid="recipe-description-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>{t('recipe.cuisine_label')} *</Label>
                    <Select value={newRecipe.cuisine} onValueChange={(v) => setNewRecipe({ ...newRecipe, cuisine: v })}>
                      <SelectTrigger data-testid="recipe-cuisine-select">
                        <SelectValue placeholder={t('recipe.select_country_cuisine')} />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {ALL_COUNTRY_CUISINES.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t('recipe.cook_time_label')}</Label>
                    <Input
                      type="number"
                      min={5}
                      max={300}
                      value={newRecipe.cook_time_min}
                      onChange={(e) => setNewRecipe({ ...newRecipe, cook_time_min: e.target.value })}
                      data-testid="recipe-cooktime-input"
                    />
                  </div>
                  <div>
                    <Label>{t('recipe.servings_label')}</Label>
                    <Input
                      type="number"
                      min={1}
                      max={20}
                      value={newRecipe.servings}
                      onChange={(e) => setNewRecipe({ ...newRecipe, servings: e.target.value })}
                      data-testid="recipe-servings-input"
                    />
                  </div>
                </div>
              </div>

              {/* Diet Tags */}
              <div>
                <Label>{t('recipe.diet_tags_label')}</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {DIET_OPTIONS.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleDietTag(tag)}
                      className={`px-3 py-1 rounded-full text-sm transition-colors ${
                        newRecipe.dietary_tags.includes(tag)
                          ? 'bg-success text-white'
                          : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                      data-testid={`diet-tag-${tag}`}
                    >
                      {t(`recipes.${getTagKey(tag)}`) || tag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Ingredients */}
              <div>
                <Label>{t('recipe.ingredients_label')} *</Label>
                <div className="space-y-2 mt-2">
                  {newRecipe.ingredients.map((ing, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <Input
                        placeholder={t('recipe.ingredient_name')}
                        value={ing.name}
                        onChange={(e) => handleIngredientChange(idx, 'name', e.target.value)}
                        className="flex-1"
                        data-testid={`ingredient-name-${idx}`}
                      />
                      <Input
                        type="number"
                        placeholder="100"
                        value={ing.amount}
                        onChange={(e) => handleIngredientChange(idx, 'amount', e.target.value)}
                        className="w-20"
                        data-testid={`ingredient-amount-${idx}`}
                      />
                      <Select value={ing.unit} onValueChange={(v) => handleIngredientChange(idx, 'unit', v)}>
                        <SelectTrigger className="w-20" data-testid={`ingredient-unit-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="g">g</SelectItem>
                          <SelectItem value="kg">kg</SelectItem>
                          <SelectItem value="ml">ml</SelectItem>
                          <SelectItem value="l">l</SelectItem>
                          <SelectItem value="stuks">{t('recipe.pieces')}</SelectItem>
                          <SelectItem value="el">el</SelectItem>
                          <SelectItem value="tl">tl</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveIngredient(idx)}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`remove-ingredient-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddIngredient}
                    className="w-full"
                    data-testid="add-ingredient-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('recipe.add_ingredient')}
                  </Button>
                </div>
              </div>

              {/* Steps */}
              <div>
                <Label>{t('recipe.steps_label')} *</Label>
                <div className="space-y-2 mt-2">
                  {newRecipe.steps.map((step, idx) => (
                    <div key={idx} className="flex gap-2 items-start">
                      <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm mt-2">
                        {idx + 1}
                      </span>
                      <Textarea
                        value={step}
                        onChange={(e) => handleStepChange(idx, e.target.value)}
                        placeholder={t('recipe.step_placeholder', { num: idx + 1 })}
                        rows={2}
                        className="flex-1"
                        data-testid={`step-input-${idx}`}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveStep(idx)}
                        className="text-red-500 hover:text-red-700 mt-2"
                        data-testid={`remove-step-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddStep}
                    className="w-full"
                    data-testid="add-step-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('recipe.add_step')}
                  </Button>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowAddModal(false); resetForm(); }}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSubmitRecipe} disabled={submitting} data-testid="submit-recipe-btn">
                {submitting ? t('common.loading') : t('recipe.save_recipe')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default RecipesPage;
